/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { parse } from '../../../util/urlHelpers';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import { SEARCH_PARAM_NAME_CATEGORY } from '../filters/CategoryFilter';
import { navigateWithCategory } from './navigateWithCategory';

import classNames from 'classnames';
import DropdownMultiEnum from '../../DropdownMultiEnum/DropdownMultiEnum';
import css from './DropdownFilterEnum.module.css';

const DropdownFilterEnum = props => {
  const {
    options = [],
    onChange = () => {},
    label = null,
    searchParamName: defaultSearchParamName,
    placeholder = null,
    customIcon = null,
    className = '',
    dropdownInputClassName = '',
    inputIcon = null,
    disableInternalSort = false,
    ...rest
  } = props;

  const location = useLocation();
  const { pathname, search, state } = location;
  const history = useHistory();
  const parsed = parse(search);
  const match = useRouteMatch();

  // Helper: get param name for an option (option-level override or component prop)
  const paramNameForOption = option => option.searchParamName || defaultSearchParamName;

  // Build initial selected value array by checking all option-related params in the url
  const initialValue = (() => {
    if (!options || !options.length) return [];

    const selected = [];

    // for each option, check if its param in URL contains the option.value
    options.forEach(opt => {
      const paramName = paramNameForOption(opt);
      const paramValue = parsed[paramName];

      if (Array.isArray(paramValue)) {
        if (paramValue.includes(opt.value)) selected.push(opt.value);
      } else if (typeof paramValue === 'string' || typeof paramValue === 'number') {
        if (String(paramValue) === String(opt.value)) selected.push(opt.value);
      }
      // else undefined -> not selected
    });

    return selected;
  })();

  const value = initialValue;

  const handleValueChanged = selectedValues => {
    // selectedValues = array of option.value strings (from DropdownMultiEnum)
    const parsedSearch = parse(search);
    const { page, ...rest } = parsedSearch;
    const normalizedSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];

    // Remove all option-related param keys from rest (we will rebuild them)
    // Collect set of all param names used by options
    const paramNamesSet = new Set(options.map(paramNameForOption));
    paramNamesSet.forEach(param => {
      if (rest.hasOwnProperty(param)) {
        delete rest[param];
      }
    });

    // Map selected option values to their param names
    const map = {}; // paramName => [values]
    normalizedSelectedValues.forEach(val => {
      // find option object for this value
      const opt = options.find(o => String(o.value) === String(val));
      const paramName = opt ? paramNameForOption(opt) : defaultSearchParamName;
      if (!map[paramName]) map[paramName] = [];
      map[paramName].push(val);
    });

    // Put grouped arrays back to rest
    Object.keys(map).forEach(paramName => {
      // If you prefer single value without array when length===1, change below
      rest[paramName] = map[paramName];
    });

    // Only a category filter decides which SEO page the visitor stays on.
    const isCategoryFilter =
      defaultSearchParamName === SEARCH_PARAM_NAME_CATEGORY ||
      options.some(opt => paramNameForOption(opt) === SEARCH_PARAM_NAME_CATEGORY);

    // Value might be 'category' or 'category/type', so extract just the category part
    const selectedCategoryValue = normalizedSelectedValues[0];
    const selectedCategory =
      isCategoryFilter && selectedCategoryValue
        ? selectedCategoryValue.split('/')[0]
        : null;

    const { redirected, search: searchString } = navigateWithCategory({
      history,
      match,
      pathname,
      state,
      params: rest,
      selectedCategory,
    });
    onChange?.(redirected ? value : normalizedSelectedValues, searchString);
  };

  return (
    <div className={classNames(css.root, className)}>
      {label && <label>{label}</label>}
      <DropdownMultiEnum
        className={css.dropdown}
        options={options}
        value={value}
        onChange={handleValueChanged}
        placeholder={placeholder}
        disabled={!options?.length}
        dropdownInputClassName={classNames(css.input, dropdownInputClassName)}
        customIcon={customIcon}
        inputIcon={inputIcon}
        disableInternalSort={disableInternalSort}
        {...rest}
      />
    </div>
  );
};

DropdownFilterEnum.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      option: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      searchString: PropTypes.string,
      // optional override for which query param to use for this option
      searchParamName: PropTypes.string,
    })
  ),
  onChange: PropTypes.func,
  onValueReset: PropTypes.func,
  label: PropTypes.node,
  // default param name used when option.searchParamName is not provided
  searchParamName: PropTypes.string.isRequired,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  customIcon: PropTypes.node,
  className: PropTypes.string,
  dropdownInputClassName: PropTypes.string,
  inputIcon: PropTypes.node,
  disableInternalSort: PropTypes.bool,
};

export default DropdownFilterEnum;
